import { useCallback, useEffect, useState } from "react"
import { tailwindClassForColoredButton } from "../../Helper/utils"
import {
    addAiTokenPrice,
    deactivateAiTokenPrice,
    fetchAllAiTokenPrices,
    updateAiTokenPrice,
} from "../../HTTP/aiTokenPrices"

const EMPTY_FORM = { title: "", token_amount: "", serper_amount: "0", amount: "", currency: "eur", active: 1 }

function validate(form) {
    const errors = []
    const title = form.title.trim()
    if (!title) errors.push("Le titre est requis.")
    if (title.length > 255) errors.push("Le titre ne doit pas dépasser 255 caractères.")
    const tokens = String(form.token_amount).trim()
    const searches = String(form.serper_amount).trim() || "0"
    if (!/^\d+$/.test(tokens)) errors.push("Le nombre de tokens doit être un entier positif ou nul.")
    if (!/^\d+$/.test(searches)) errors.push("Le nombre de recherches doit être un entier positif ou nul.")
    if (/^\d+$/.test(tokens) && /^\d+$/.test(searches) && Number(tokens) === 0 && Number(searches) === 0) {
        errors.push("Le pack doit contenir au moins des tokens ou des recherches.")
    }
    if (!/^\d+(\.\d{1,2})?$/.test(String(form.amount).trim())) errors.push("Le montant doit avoir au plus deux décimales.")
    if (!/^[a-zA-Z]{3}$/.test(form.currency.trim())) errors.push("La devise doit être un code de 3 lettres (ex. eur).")
    return errors
}

export default function AiTokenPricesAdmin() {
    const localUser = JSON.parse(localStorage.getItem("user"))
    if (!localUser.super_admin && localUser.admin_access !== 2) {
        window.location.href = "/"
    }

    const [prices, setPrices] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState("")
    const [message, setMessage] = useState("")
    const [editing, setEditing] = useState(null) // null = closed, "new" or the price being edited
    const [form, setForm] = useState(EMPTY_FORM)
    const [formErrors, setFormErrors] = useState([])
    const [saving, setSaving] = useState(false)

    const load = useCallback(async () => {
        setLoading(true)
        const res = await fetchAllAiTokenPrices()
        if (res.ok) {
            setPrices(res.data)
            setError("")
        } else {
            setError(res.error)
        }
        setLoading(false)
    }, [])

    useEffect(() => {
        load()
    }, [load])

    const openNew = () => {
        setEditing("new")
        setForm(EMPTY_FORM)
        setFormErrors([])
        setMessage("")
    }

    const openEdit = (price) => {
        setEditing(price)
        setForm({
            title: price.title ?? "",
            token_amount: String(price.token_amount ?? ""),
            serper_amount: String(price.serper_amount ?? 0),
            amount: String(price.amount ?? ""),
            currency: price.currency ?? "",
            active: Number(price.active) === 1 ? 1 : 0,
        })
        setFormErrors([])
        setMessage("")
    }

    const closeForm = () => {
        setEditing(null)
        setFormErrors([])
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        const errors = validate(form)
        if (errors.length) {
            setFormErrors(errors)
            return
        }
        const values = {
            title: form.title.trim(),
            token_amount: Number(form.token_amount),
            serper_amount: Number(form.serper_amount || 0),
            amount: Number(form.amount),
            currency: form.currency.trim().toLowerCase(),
            active: Number(form.active),
        }

        setSaving(true)
        let res
        if (editing === "new") {
            res = await addAiTokenPrice(values)
        } else {
            const changes = { id: editing.id }
            Object.keys(values).forEach((key) => {
                const original =                 ["token_amount", "serper_amount", "amount", "active"].includes(key)
                                    ? Number(editing[key] ?? 0)
                    : editing[key]
                if (values[key] !== original) changes[key] = values[key]
            })
            if (Object.keys(changes).length === 1) {
                setSaving(false)
                setFormErrors(["Aucune modification à enregistrer."])
                return
            }
            res = await updateAiTokenPrice(changes)
        }
        setSaving(false)

        if (res.ok) {
            setMessage(editing === "new" ? "Tarif créé." : "Tarif mis à jour.")
            closeForm()
            load()
        } else {
            setFormErrors(res.error.split("\n"))
            if (res.status === 404) load()
        }
    }

    const toggleActive = async (price) => {
        setMessage("")
        setError("")
        const isActive = Number(price.active) === 1
        if (isActive && !window.confirm("Désactiver ce tarif ? Il ne sera plus proposé aux utilisateurs.")) return

        const res = isActive
            ? await deactivateAiTokenPrice(price.id)
            : await updateAiTokenPrice({ id: price.id, active: 1 })
        if (res.ok) {
            setMessage(isActive ? "Tarif désactivé." : "Tarif réactivé.")
            load()
        } else {
            setError(res.error)
            if (res.status === 404) load()
        }
    }

    const setField = (key) => (e) => setForm({ ...form, [key]: e.target.value })
    const inputClass = "border border-gray-300 rounded px-2 py-1 w-full"

    return (
        <div className="flex mt-8">
            <div className="flex-grow px-4">
                <h1 className="text-center mt-4 text-2xl font-bold">Tarifs des tokens IA</h1>
                <p className="text-center text-sm text-gray-500">
                    Liste complète (tarifs actifs et inactifs). Les utilisateurs ne voient que les tarifs actifs.
                </p>

                <button className={tailwindClassForColoredButton("green") + " mt-4 mb-4"} onClick={openNew}>
                    Ajouter un tarif
                </button>

                {message && <div className="mb-4 p-2 bg-green-100 text-green-800 rounded" role="status">{message}</div>}
                {error && <div className="mb-4 p-2 bg-red-100 text-red-800 rounded whitespace-pre-line" role="alert">{error}</div>}

                {editing !== null && (
                    <form onSubmit={handleSubmit} className="mb-6 p-4 border rounded max-w-xl space-y-3">
                        <h2 className="font-bold">{editing === "new" ? "Nouveau tarif" : `Modifier le tarif #${editing.id}`}</h2>
                        {formErrors.length > 0 && (
                            <ul className="p-2 bg-red-100 text-red-800 rounded list-disc list-inside" role="alert">
                                {formErrors.map((msg, i) => <li key={i}>{msg}</li>)}
                            </ul>
                        )}
                        <label className="block">Titre
                            <input className={inputClass} type="text" maxLength={255} value={form.title} onChange={setField("title")} />
                        </label>
                        <label className="block">Nombre de tokens
                            <input className={inputClass}                             type="number" min="0" step="1" value={form.token_amount} onChange={setField("token_amount")} />
                        </label>
                                                    <label className="block">Nombre de recherches Serper
                                                        <input className={inputClass} type="number" min="0" step="1" value={form.serper_amount} onChange={setField("serper_amount")} />
                                                    </label>
                        <label className="block">Montant
                            <input className={inputClass} type="number" min="0" step="0.01" value={form.amount} onChange={setField("amount")} />
                        </label>
                        <label className="block">Devise
                            <input className={inputClass} type="text" maxLength={3} value={form.currency} onChange={setField("currency")} />
                        </label>
                        <label className="block">État
                            <select className={inputClass} value={form.active} onChange={(e) => setForm({ ...form, active: Number(e.target.value) })}>
                                <option value={1}>Actif</option>
                                <option value={0}>Inactif</option>
                            </select>
                        </label>
                        <div className="flex gap-2">
                            <button type="submit" disabled={saving} className={tailwindClassForColoredButton("green")}>
                                {saving ? "Enregistrement..." : "Enregistrer"}
                            </button>
                            <button type="button" onClick={closeForm} className={tailwindClassForColoredButton("red")}>
                                Annuler
                            </button>
                        </div>
                    </form>
                )}

                {loading ? (
                    <p>Chargement...</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="border-b">
                                    <th className="p-2">ID</th>
                                    <th className="p-2">Titre</th>
                                    <th className="p-2">Tokens</th>
                                    <th className="p-2">Recherches</th>
                                    <th className="p-2">Montant</th>
                                    <th className="p-2">Devise</th>
                                    <th className="p-2">État</th>
                                    <th className="p-2">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {prices.length === 0 && (
                                    <tr><td className="p-2" colSpan={8}>Aucun tarif.</td></tr>
                                )}
                                {prices.map((price) => {
                                    const isActive = Number(price.active) === 1
                                    return (
                                        <tr key={price.id} className={"border-b" + (isActive ? "" : " text-gray-400")}>
                                            <td className="p-2">{price.id}</td>
                                            <td className="p-2">{price.title}</td>
                                            <td className="p-2">{Number(price.token_amount).toLocaleString("fr-FR")}</td>
                                            <td className="p-2">{Number(price.serper_amount ?? 0).toLocaleString("fr-FR")}</td>
                                            <td className="p-2">{Number(price.amount).toFixed(2)}</td>
                                            <td className="p-2 uppercase">{price.currency}</td>
                                            <td className="p-2">{isActive ? "Actif" : "Inactif"}</td>
                                            <td className="p-2 flex gap-2">
                                                <button className={tailwindClassForColoredButton("blue")} onClick={() => openEdit(price)}>
                                                    Modifier
                                                </button>
                                                <button
                                                    className={tailwindClassForColoredButton(isActive ? "red" : "green")}
                                                    onClick={() => toggleActive(price)}
                                                >
                                                    {isActive ? "Désactiver" : "Réactiver"}
                                                </button>
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    )
}
